import './PremiumGameUpgradePrompt.css';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo } from '@lub0v/santa-utils';
import { Button, Stack, Typography } from '@src/components';
import { Switch } from '@src/components/Switch/Switch';
import { showDialog, closeDialog, useDialogOpen } from '@src/components/Dialog/Dialog';
import { useAppDispatch, useAppSelector } from '@src/state/hooks';
import { loadBox } from '@src/state/boxSlice';

const DISMISSED_KEY = 'santa:premium-game-upgrade:dont-show-again';
function wasDismissed() {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}
function UpgradeBody({ boxKey }: { boxKey: string }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [remember, setRemember] = useState(false);
  function dismiss() {
    closeDialog();
  }
  async function upgrade() {
    setBusy(true);
    setError(false);
    try {
      const response = await fetch(`/api/box/${encodeURIComponent(boxKey)}/upgrade-premium`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('Upgrade failed');
      await dispatch(loadBox({ key: boxKey })).unwrap();
      closeDialog();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Stack gap={4} wide>
      <Typography as="p" role="body/base">
        {t('premiumGameUpgrade.body')}
      </Typography>
      <Switch
        label={t('premiumGameUpgrade.dontShowAgain')}
        checked={remember}
        onCheckedChange={(value) => {
          setRemember(value);
          try {
            if (value) localStorage.setItem(DISMISSED_KEY, '1');
            else localStorage.removeItem(DISMISSED_KEY);
          } catch {
            /* The dialog still works without storage. */
          }
        }}
        testId="premium-game-upgrade-remember"
      />
      {error && (
        <Typography as="p" role="body/base">
          {t('premiumGameUpgrade.error')}
        </Typography>
      )}
      <div className="premium-game-upgrade__actions">
        <Button fullWidth disabled={busy} onClick={upgrade} testId="premium-game-upgrade-submit">
          {t('premiumGameUpgrade.upgrade')}
        </Button>
        <Button
          fullWidth
          variant="secondary"
          disabled={busy}
          onClick={dismiss}
          testId="premium-game-upgrade-later"
        >
          {t('premiumGameUpgrade.later')}
        </Button>
      </div>
    </Stack>
  );
}
export function PremiumGameUpgradePrompt({
  info,
  enabled,
}: {
  info: BoxInfo | null;
  enabled: boolean;
}) {
  const user = useAppSelector((state) => state.session.user);
  const { t } = useTranslation();
  const open = useDialogOpen();
  const shown = useRef(false);
  useEffect(() => {
    if (
      !enabled ||
      open ||
      shown.current ||
      !info ||
      !user?.isPremium ||
      info.box.isPremium ||
      info.box.isArchived ||
      info.box.year !== String(new Date().getUTCFullYear()) ||
      !(info.isAdmin || info.isParticipant) ||
      wasDismissed()
    )
      return;
    shown.current = true;
    showDialog({
      title: t('premiumGameUpgrade.title'),
      testId: 'premium-game-upgrade',
      children: <UpgradeBody boxKey={info.box.key} />,
    });
  }, [enabled, open, info, user, t]);
  return null;
}
